import { GameEngine } from './engine/game-engine.js';
import { DebugOverlay } from './ui/debug-overlay.js';
import { NotificationLog } from './ui/notification-log.js';
import { InputManager } from './character/input-manager.js';
import { SlimeController } from './character/slime-controller.js';
import { SlimeMesh } from './character/slime-mesh.js';
import { PickupManager } from './world/pickup-manager.js';
import { ResourceNodeManager } from './world/resource-node-manager.js';
import { CraftingPanel } from './ui/crafting-panel.js';
import { SettlementManager } from './settlement/settlement-manager.js';
import { ProjectileManager } from './combat/projectile-manager.js';
import { CombatHUD } from './ui/combat-hud.js';
import { DungeonArena } from './dungeon/dungeon-arena.js';
import { BossMesh } from './combat/boss-mesh.js';
import {
  DissolutionEngine,
  MutationSystem,
  Inventory,
  CraftingEngine,
  SettlementGrid,
  CombatSkillManager,
  BossEncounter,
  SaveEngine,
  QuestTracker
} from '@slimebound/core';
import { GameSaveService } from './save/game-save-service.js';
import { CloudSaveSync } from './save/cloud-save-sync.js';
import { AudioSynthesizer } from './audio/audio-synthesizer.js';
import { CameraShake } from './camera/camera-shake.js';
import { QuestHUD } from './ui/quest-hud.js';
import { VictoryModal } from './ui/victory-modal.js';
import { MeshBuilder, StandardMaterial, Color3 } from '@babylonjs/core';

window.addEventListener('DOMContentLoaded', () => {
  const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
  if (!canvas) {
    console.error('Canvas #renderCanvas not found in DOM.');
    return;
  }

  const game = new GameEngine(canvas);
  const debugOverlay = new DebugOverlay();
  const notificationLog = new NotificationLog();
  const inputManager = new InputManager();
  const slimeController = new SlimeController({ radius: 0.9 });
  const slimeMesh = new SlimeMesh(game.scene, { radius: 0.9 });
  const pickupManager = new PickupManager();
  const dissolutionEngine = new DissolutionEngine();
  const mutationSystem = new MutationSystem();
  const inventory = new Inventory({ slotCount: 16, maxMassKg: 120.0 });
  const audio = new AudioSynthesizer();
  const cameraShake = new CameraShake();
  const questTracker = new QuestTracker();
  const questHUD = new QuestHUD(questTracker);
  const victoryModal = new VictoryModal();
  let totalPlaytime = 0;

  questHUD.render();

  questTracker.onQuestCompleted((q) => {
    notificationLog.add(`📜 Quest Completed: [${q.title}]!`, 'unlock', 5.0);
    audio.playVictoryFanfare();
  });

  questTracker.onVictory(() => {
    notificationLog.add('👑 ALL OBJECTIVES COMPLETE! Master of the Primordial Realm!', 'success', 10.0);
    audio.playVictoryFanfare();
    victoryModal.show({
      playtimeSec: totalPlaytime,
      slimeMassKg: slimeController.state.massKg,
      mutationsUnlocked: mutationSystem.getUnlockedTraits().length,
      bossDefeated: 'Crystal Golem'
    });
  });

  const btnAudio = document.getElementById('btn-toggle-audio');
  btnAudio?.addEventListener('click', () => {
    const isMuted = audio.isMuted();
    audio.setMuted(!isMuted);
    btnAudio.textContent = isMuted ? '🔊 Audio' : '🔇 Muted';
    btnAudio.title = isMuted ? 'Mute Sound' : 'Unmute Sound';
  });

  const craftingEngine = new CraftingEngine();
  const craftingPanel = new CraftingPanel(craftingEngine, inventory, (msg) => {
    notificationLog.add(msg, 'success', 4.0);
    slimeController.wobble.applyImpulse(0.3);
    questTracker.recordBuilding();
    audio.playSpell('water_shot');
  });
  const resourceNodeManager = new ResourceNodeManager();
  const settlementGrid = new SettlementGrid();
  const settlementManager = new SettlementManager(settlementGrid, inventory, (msg) => {
    notificationLog.add(msg, 'unlock', 4.5);
    slimeController.wobble.applyImpulse(0.35);
    questTracker.recordTaming();
    audio.playAbsorb();
  });

  // Combat, Abilities & Dungeon
  const combatSkillManager = new CombatSkillManager();
  const combatHUD = new CombatHUD(combatSkillManager);
  const projectileManager = new ProjectileManager();
  const dungeonArena = new DungeonArena();
  dungeonArena.spawnArenaEnvironment(game.scene, game.sceneManager.shadowGenerator);

  const bossSpawn = dungeonArena.getBossSpawnPosition();
  const bossEncounter = new BossEncounter({
    id: 'boss_crystal_golem',
    name: 'Crystal Golem',
    maxHp: 250,
    position: bossSpawn
  });
  const bossMesh = new BossMesh(game.scene, game.sceneManager.shadowGenerator);
  bossMesh.setPosition(bossSpawn.x, bossSpawn.y, bossSpawn.z);

  // Slime casts real-time shadow onto terrain
  game.sceneManager.registerShadowCaster(slimeMesh.bodyMesh);

  // Spawn camp foundation, world pickups, and harvestable resource nodes
  settlementManager.spawnCampWorld(game.scene, game.sceneManager.shadowGenerator);
  pickupManager.spawnWorldPickups(game.scene, game.sceneManager.shadowGenerator);
  resourceNodeManager.spawnResourceNodes(game.scene, game.sceneManager.shadowGenerator);

  // Welcome notification
  notificationLog.add(
    '🟢 Reborn as a Slime! WASD: Move | Space: Hop | 1/2/3: Skills | F: Harvest / Feed | Top-Right: Crafting',
    'info',
    7.0
  );

  // Add decorative monolith obstacles casting shadows across the terrain
  const monolithPositions = [
    { x: -8, z: 10, height: 4 },
    { x: 10, z: -6, height: 6 },
    { x: -12, z: -12, height: 3.5 },
    { x: 14, z: 14, height: 5 }
  ];

  const monolithMat = new StandardMaterial('monolithMat', game.scene);
  monolithMat.diffuseColor = new Color3(0.06, 0.08, 0.12);
  monolithMat.specularColor = new Color3(0.2, 0.3, 0.4);

  monolithPositions.forEach((pos, idx) => {
    const monolith = MeshBuilder.CreateBox(
      `monolith_${idx}`,
      { width: 1.4, depth: 1.4, height: pos.height },
      game.scene
    );
    monolith.position.set(pos.x, pos.height / 2, pos.z);
    monolith.material = monolithMat;
    monolith.checkCollisions = true;
    game.sceneManager.registerShadowCaster(monolith);
  });

  // Attach crafting click listener
  const craftingContainer = document.getElementById('hud-crafting-panel');
  if (craftingContainer) {
    craftingContainer.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target && target.matches('.craft-btn')) {
        const recipeId = target.getAttribute('data-recipe-id');
        if (recipeId) {
          craftingPanel.craft(recipeId);
        }
      }
    });
  }

  // Persistence & Save Engine
  const gameSaveService = new GameSaveService(undefined, {
    intervalSec: 30.0,
    onSave: () => {
      notificationLog.add('💾 Auto-saved game progress', 'info', 2.5);
    }
  });

  // Attempt restoring existing save on boot
  const existingSave = gameSaveService.loadGame();
  if (existingSave) {
    gameSaveService.applySaveToGame(existingSave, {
      slime: slimeController.state,
      slimeController,
      slimeMesh,
      inventory,
      mutations: mutationSystem,
      settlementManager,
      bossEncounter,
      notificationLog
    });
  }

  // Save / Load UI Button Listeners
  const btnSave = document.getElementById('btn-save-game');
  btnSave?.addEventListener('click', () => {
    gameSaveService.saveGame({
      slime: slimeController.state,
      position: slimeController.position,
      inventory,
      mutations: mutationSystem,
      settlement: settlementGrid,
      boss: bossEncounter
    });
    notificationLog.add('💾 Progress saved manually!', 'success', 3.0);
  });

  const btnLoad = document.getElementById('btn-load-game');
  btnLoad?.addEventListener('click', () => {
    const save = gameSaveService.loadGame();
    if (save) {
      gameSaveService.applySaveToGame(save, {
        slime: slimeController.state,
        slimeController,
        slimeMesh,
        inventory,
        mutations: mutationSystem,
        settlementManager,
        bossEncounter,
        notificationLog
      });
    } else {
      notificationLog.add('⚠️ No saved data found in storage.', 'warning', 3.0);
    }
  });

  const btnExport = document.getElementById('btn-export-save');
  btnExport?.addEventListener('click', () => {
    gameSaveService.downloadSaveFile({
      slime: slimeController.state,
      position: slimeController.position,
      inventory,
      mutations: mutationSystem,
      settlement: settlementGrid,
      boss: bossEncounter
    });
    notificationLog.add('⬇️ Save file exported!', 'success', 3.0);
  });

  const btnImport = document.getElementById('btn-import-save');
  const inputImport = document.getElementById('input-import-save') as HTMLInputElement;
  btnImport?.addEventListener('click', () => {
    inputImport?.click();
  });

  inputImport?.addEventListener('change', () => {
    const file = inputImport.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const imported = gameSaveService.importSaveFromJson(content);
          gameSaveService.applySaveToGame(imported, {
            slime: slimeController.state,
            slimeController,
            slimeMesh,
            inventory,
            mutations: mutationSystem,
            settlementManager,
            bossEncounter,
            notificationLog
          });
          notificationLog.add('⬆️ Save file imported successfully!', 'success', 4.0);
        } catch {
          notificationLog.add('❌ Failed to parse imported save file.', 'warning', 4.0);
        }
      };
      reader.readAsText(file);
    }
  });

  const cloudSaveSync = new CloudSaveSync({
    apiUrl: window.location.hostname === 'localhost' ? '/api' : 'https://slimebound-api.e24qdy.workers.dev/api',
    syncIntervalSec: 300,
    onSyncSuccess: (info) => {
      notificationLog.add(`☁️ Cloud save synced to D1 (Player: ${info.playerId.slice(0, 10)}...)`, 'success', 3.5);
    },
    onSyncError: (err) => {
      notificationLog.add(`☁️ Cloud note: ${err}`, 'info', 3.0);
    }
  });

  const btnCloud = document.getElementById('btn-cloud-sync');
  btnCloud?.addEventListener('click', async () => {
    btnCloud.textContent = '⏳ Syncing...';
    const serializedJson = SaveEngine.exportToJsonString({
      slime: slimeController.state,
      position: slimeController.position,
      inventory,
      mutations: mutationSystem,
      settlement: settlementGrid,
      boss: bossEncounter
    });
    const res = await cloudSaveSync.syncToCloud(serializedJson, {
      playtimeSec: totalPlaytime,
      slimeMassKg: slimeController.state.massKg
    });
    btnCloud.textContent = '☁️ Cloud';
    if (res.success) {
      notificationLog.add('☁️ Cloud save verified on Cloudflare D1!', 'success', 3.5);
    } else {
      notificationLog.add(`☁️ Cloud sync notice: ${res.error}`, 'warning', 3.5);
    }
  });

  let renderCraftTimer = 0;

  // Start engine loop with character kinematics, combat, camp simulation & metrics
  game.start((metrics, dt) => {
    // AutoSave loop update (every 30s local)
    gameSaveService.getAutoSaveController().update(dt, () => {
      return SaveEngine.serialize({
        slime: slimeController.state,
        position: slimeController.position,
        inventory,
        mutations: mutationSystem,
        settlement: settlementGrid,
        boss: bossEncounter
      });
    });

    // Cloudflare D1 cloud save auto-sync (debounced 300s)
    cloudSaveSync.update(dt, () => {
      return {
        saveData: SaveEngine.exportToJsonString({
          slime: slimeController.state,
          position: slimeController.position,
          inventory,
          mutations: mutationSystem,
          settlement: settlementGrid,
          boss: bossEncounter
        }),
        playtimeSec: totalPlaytime,
        slimeMassKg: slimeController.state.massKg
      };
    });

    totalPlaytime += dt;

    // Jump audio trigger
    if (inputManager.isJumping() && slimeController.isGrounded) {
      audio.playJump();
    }

    // Camera orbital yaw orientation relative to player movement
    const cameraYaw = game.camera.alpha + Math.PI / 2;

    slimeController.update(dt, inputManager, cameraYaw);
    slimeMesh.update(
      slimeController.position,
      slimeController.velocity,
      slimeController.wobble.getScale(),
      dt
    );

    // Track exploration distance for first quest
    const movedDist = Math.hypot(slimeController.velocity.x, slimeController.velocity.z) * dt;
    if (movedDist > 0.005) {
      questTracker.recordMovement(movedDist);
      questHUD.render();
    }

    // Third-person camera smoothly tracks the slime position
    game.cameraController.setTarget(
      slimeController.position.x,
      slimeController.position.y + 0.3,
      slimeController.position.z
    );

    // Apply procedural camera shake trauma
    cameraShake.update(dt);
    game.cameraController.setShakeOffset(cameraShake.getOffset());

    // Ability Combat Casting
    const requestedSkill = inputManager.consumeSkill();
    if (requestedSkill) {
      const castResult = combatSkillManager.cast(requestedSkill, slimeController.state);
      if (castResult.success && castResult.skill) {
        slimeController.wobble.applyImpulse(0.35);
        audio.playSpell(requestedSkill);

        // Calculate aim direction from slime forward
        const aimDir = {
          x: Math.sin(cameraYaw),
          y: 0,
          z: Math.cos(cameraYaw)
        };

        if (requestedSkill === 'water_shot' || requestedSkill === 'acid_spit') {
          projectileManager.spawnProjectile(
            {
              x: slimeController.position.x,
              y: slimeController.position.y + 0.5,
              z: slimeController.position.z
            },
            aimDir,
            {
              id: requestedSkill,
              damage: castResult.skill.baseDamage,
              speed: 18.0,
              element: castResult.skill.element,
              lifetime: 2.2
            },
            game.scene,
            game.sceneManager.shadowGenerator
          );
          notificationLog.add(`Cast ${castResult.skill.name}!`, 'info', 1.8);
        } else if (requestedSkill === 'body_slam') {
          slimeController.wobble.applyImpulse(0.8);
          cameraShake.addTrauma(0.5);
          notificationLog.add('💥 BODY SLAM IMPACT!', 'unlock', 2.0);

          // Area of effect hit check
          const distToBoss = Math.hypot(
            slimeController.position.x - bossEncounter.position.x,
            slimeController.position.z - bossEncounter.position.z
          );
          if (distToBoss <= 5.5 && !bossEncounter.isDefeated) {
            const hit = bossEncounter.takeDamage(castResult.skill.baseDamage);
            audio.playHit();
            notificationLog.add(
              `Slammed Crystal Golem for ${castResult.skill.baseDamage} damage! [HP: ${hit.currentHp}/${bossEncounter.maxHp}]`,
              'info',
              2.5
            );
            if (hit.phaseChanged) {
              audio.playBossRoar();
              cameraShake.addTrauma(0.8);
              notificationLog.add('⚠️ BOSS ENRAGED! Crystal Golem enters Phase 2!', 'unlock', 5.0);
            }
            if (hit.defeated) {
              handleBossDefeat();
            }
          }
        }
      }
    }

    // Interaction handling on key press 'F' / 'E'
    if (inputManager.consumeInteract(dt)) {
      const nearMonster = settlementManager.checkMonsterProximity(
        slimeController.position,
        slimeController.radius
      );
      if (nearMonster) {
        settlementManager.interactFeedMonster(nearMonster.id);
        slimeController.wobble.applyImpulse(0.3);
      } else {
        const struck = resourceNodeManager.checkHarvestInteraction(
          slimeController.position,
          slimeController.radius,
          inventory,
          notificationLog
        );
        if (struck) {
          slimeController.wobble.applyImpulse(0.25);
          audio.playHit();
        }
      }
    }

    // Update projectile physics and hit testing against boss
    const combatTargets = bossEncounter.isDefeated
      ? []
      : [
          {
            id: bossEncounter.id,
            position: bossEncounter.position,
            radius: 2.0,
            currentHp: bossEncounter.currentHp,
            takeDamage: (dmg: number) => {
              const hit = bossEncounter.takeDamage(dmg);
              audio.playHit();
              cameraShake.addTrauma(0.25);
              notificationLog.add(
                `Direct hit on Crystal Golem! [${hit.currentHp}/${bossEncounter.maxHp} HP]`,
                'info',
                2.0
              );
              if (hit.phaseChanged) {
                audio.playBossRoar();
                cameraShake.addTrauma(0.8);
                notificationLog.add('⚠️ BOSS ENRAGED! Crystal Golem enters Phase 2!', 'unlock', 5.0);
              }
              if (hit.defeated) {
                handleBossDefeat();
              }
            }
          }
        ];

    projectileManager.update(dt, combatTargets);
    combatSkillManager.update(dt);
    combatHUD.render(slimeController.state.gelEnergy);

    // Update boss encounter AI & mesh
    const bossTick = bossEncounter.update(dt);
    bossMesh.update(dt, bossTick.isTelegraphing, bossEncounter.isEnraged, bossEncounter.isDefeated);
    if (bossTick.attackTriggered) {
      const dist = Math.hypot(
        slimeController.position.x - bossEncounter.position.x,
        slimeController.position.z - bossEncounter.position.z
      );
      if (dist <= 6.0) {
        slimeController.wobble.applyImpulse(0.6);
        audio.playHit();
        cameraShake.addTrauma(0.7);
        notificationLog.add('💥 Crystal Golem executed Shockwave Slam!', 'unlock', 2.5);
      }
    }

    function handleBossDefeat() {
      questTracker.recordBossDefeat();
      questHUD.render();
      audio.playVictoryFanfare();
      cameraShake.addTrauma(1.0);
      notificationLog.add(
        '🏆 BOSS DEFEATED! The Crystal Golem has crumbled into crystalline relics!',
        'success',
        8.0
      );
      const drop = bossEncounter.getDropLoot();
      if (drop) {
        dissolutionEngine.ingest({
          id: drop.itemId,
          name: drop.name,
          materialType: 'mineral',
          massKg: drop.massKg,
          gelEnergyYield: 40,
          biomassYield: 25
        });
        inventory.addItem({
          itemId: drop.itemId,
          name: drop.name,
          quantity: 1,
          unitMassKg: drop.massKg
        });
      }
    }

    resourceNodeManager.update(dt);
    settlementManager.update(dt);

    // Pickups and absorption check
    pickupManager.update(dt);
    const absorbed = pickupManager.checkProximity(slimeController.position, slimeController.radius);
    for (const item of absorbed) {
      dissolutionEngine.ingest(item);
      inventory.addItem({
        itemId: item.id,
        name: item.name,
        quantity: 1,
        unitMassKg: item.massKg
      });
      notificationLog.add(`Engulfed ${item.name}! Beginning dissolution...`, 'info', 2.5);
      slimeController.wobble.applyImpulse(0.2);
      audio.playAbsorb();
      questTracker.recordAbsorption();
      questHUD.render();
    }

    // Process internal dissolution buffer
    const digested = dissolutionEngine.update(dt, slimeController.state);
    for (const res of digested) {
      notificationLog.add(
        `✓ Digested ${res.item.name}! +${res.gainedMassKg}kg Mass, +${res.restoredEnergy} Energy`,
        'success',
        3.5
      );
      slimeController.wobble.applyImpulse(0.15);

      // Check mutation and trait unlocks
      const newTraits = mutationSystem.recordAbsorption(res.item);
      for (const trait of newTraits) {
        questTracker.recordMutation();
        questHUD.render();
        audio.playVictoryFanfare();
        notificationLog.add(
          `✨ MUTATION UNLOCKED: [${trait.name}] — ${trait.description}`,
          'unlock',
          6.0
        );
        slimeController.wobble.applyImpulse(0.4);
      }
    }

    // Update crafting panel view periodically
    renderCraftTimer += dt;
    if (renderCraftTimer >= 0.25) {
      renderCraftTimer = 0;
      craftingPanel.setUnlockedTraits(mutationSystem.getUnlockedTraits().map((t) => t.id));
      craftingPanel.render();
    }

    // Update toast alerts
    notificationLog.update(dt);

    // Update performance HUD with slime stats
    debugOverlay.update({
      ...metrics,
      slimeEnergy: slimeController.state.gelEnergy,
      slimeMass: slimeController.state.massKg
    });
  });
});
